import React from 'react';

import { EditIcon, LocationMapPinIcon } from '@vlabs/icons';
import {
  CircleAnimatedButton, Control, GeoLocation, GeoLocationMarker, useModal,
} from '@vlabs/uikit';
import { useDynamicCoordinates } from '@vlabs/uikit/components/geo-location/useDynamicCoordinates';

import { buildLocationString } from '../location';
import type { GroupStreamSettingsLocation } from '../stream-settings';
import { GeopositionForm } from './GeopositionForm';
import { useGeopositionTranslation } from './i18n';
import st from './SourceGeoposition.module.sass';
import { SynchronizeStatus } from '../sync/SynchronizeStatus';

// В виджете координаты показываем до 4 знаков после запятой (в форме — полная точность)
function formatCoordinate(value: number | undefined): string | undefined {
  if (value === undefined) return undefined;
  return value.toFixed(4);
}

interface SourceGeopositionProps {
  /** Текущая геопозиция — родитель передаёт значение из своей формы. */
  defaultValues?: GroupStreamSettingsLocation;
  /** Возвращает данные формы геопозиции; родитель сам пишет их в свой стейт. */
  onSubmit: (next: GroupStreamSettingsLocation | undefined) => void;
  /** Статус наследования геопозиции группы; undefined — иконка не показывается. */
  syncStatus?: boolean;
}

// Блок «Геопозиция» настроек источника/группы: карта, кнопки установки/изменения,
// адрес и координаты. Не знает, в каком пути формы родителя лежат данные (ZEN #13).
function SourceGeoposition({
  defaultValues = undefined,
  onSubmit,
  syncStatus = undefined,
}: SourceGeopositionProps): React.JSX.Element {
  const { t } = useGeopositionTranslation();
  const modal = useModal({ size: 'large', scrollable: true, contentClassName: st.Modal });

  const { latitude, longitude } = defaultValues?.geo_position ?? {};
  const hasCoordinates = latitude !== undefined && longitude !== undefined;
  const formattedLocation = buildLocationString(defaultValues);
  const hasLocation = !!formattedLocation || hasCoordinates;
  const position = useDynamicCoordinates({
    latitude: latitude ?? '',
    longitude: longitude ?? '',
  });

  const onFormSubmit = (nextLocation: GroupStreamSettingsLocation | undefined) => {
    if (JSON.stringify(nextLocation ?? {}) !== JSON.stringify(defaultValues ?? {})) {
      onSubmit(nextLocation);
    }
    modal.close();
  };

  let editButton: React.JSX.Element | null = null;
  if (hasLocation) {
    editButton = (
      <div className={st.EditButton}>
        <CircleAnimatedButton
          icon={<EditIcon />}
          onClick={modal.open}
          onSurface
        >
          {t('edit_geoposition')}
        </CircleAnimatedButton>
      </div>
    );
  }

  let map: React.JSX.Element;
  if (hasCoordinates) {
    map = (
      <GeoLocation
        center={position}
        mapProps={{ mouseEvents: false, touchEvents: false }}
        toggleFullScreenMode={false}
      >
        <GeoLocationMarker anchor={position} />
      </GeoLocation>
    );
  } else {
    map = (
      <GeoLocation
        mapProps={{ mouseEvents: false, touchEvents: false }}
        toggleFullScreenMode={false}
      />
    );
  }

  let setButton: React.JSX.Element | null = null;
  if (!hasLocation) {
    setButton = (
      <Control.Button
        className={st.SetButton}
        icon={<LocationMapPinIcon />}
        kind="secondary"
        onClick={modal.open}
        variant="outlined"
      >
        {t('set_geoposition')}
      </Control.Button>
    );
  }

  let addressRow: React.JSX.Element | null = null;
  if (formattedLocation) {
    addressRow = (
      <p className={st.LocationRow}>
        <span className={st.LocationAddress}>{formattedLocation}</span>
      </p>
    );
  }

  let coordinatesRows: React.JSX.Element | null = null;
  if (hasCoordinates) {
    coordinatesRows = (
      <React.Fragment>
        <p className={st.LocationRow}>
          <span className={st.LocationLabel}>{t('longitude')}</span>
          <span>{formatCoordinate(longitude)}</span>
        </p>
        <p className={st.LocationRow}>
          <span className={st.LocationLabel}>{t('latitude')}</span>
          <span>{formatCoordinate(latitude)}</span>
        </p>
      </React.Fragment>
    );
  }

  let locationBlock: React.JSX.Element | null = null;
  if (formattedLocation || hasCoordinates) {
    locationBlock = (
      <div className={st.Location}>
        {addressRow}
        {coordinatesRows}
      </div>
    );
  }

  return (
    <div className={st.Root}>
      {modal.wrap(
        <GeopositionForm
          defaultValues={defaultValues}
          onCancel={modal.close}
          onSubmit={onFormSubmit}
        />,
      )}

      <div className={st.TitleRow}>
        <h6 className={st.Title}>{t('title')}</h6>
        {syncStatus !== undefined && <SynchronizeStatus synchronized={syncStatus} />}
      </div>

      <div className={st.Content}>
        <div className={st.MapBlock}>
          {editButton}
          {map}
        </div>

        {setButton}
        {locationBlock}
      </div>
    </div>
  );
}

export { SourceGeoposition };
